import React from 'react';

const CodingDiFinlandia: React.FC = () => {
  return (
    <div className="text-justify py-4">
      <p className="mb-4">
        <b>Laporan OECD 2024</b> mengungkap bahwa <b>97% sekolah dasar di Finlandia</b> telah mengintegrasikan coding ke dalam kurikulum sejak usia 5 tahun. Kebijakan ini membuat Finlandia menjadi <b>negara dengan literasi digital tertinggi</b> di Eropa. Apa rahasianya?
      </p>

      <h2 className="text-2xl font-bold mb-4">Bukan Tentang Jadi Programmer</h2>
      <div className="bg-purple-50 border-l-4 border-purple-300 p-4 mb-6">
        <p>
          <b>&quot;Kami tidak mencetak anak-anak jadi developer, tapi melatih cara berpikir komputasional&quot;</b> - <i>Dr. Liisa Korkealaakso</i>, Pakar Pendidikan Digital Universitas Helsinki.
        </p>
      </div>

      <p className="mb-6">
        Konsep dasar yang diajarkan di TK Finlandia:
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {[ 
          {
            title: "🐛 Sequential Logic",
            desc: "Menyusun urutan gambar cerita"
          },
          {
            title: "🔄 Problem Decomposition",
            desc: "Memecah tugas menyikat gigi menjadi langkah kecil"
          },
          {
            title: "⚡ Pattern Recognition",
            desc: "Mengelompokkan mainan berdasarkan pola warna/bentuk"
          },
          {
            title: "🎯 Algorithmic Thinking",
            desc: "Membuat 'resep' bermain peran"
          }
        ].map((item, idx) => (
          <div key={idx} className="border rounded-lg p-3 bg-white">
            <h3 className="font-bold text-lg">{item.title}</h3>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-2xl font-bold mb-4">Metode Belajar yang Menyenangkan</h2>
      <ol className="list-decimal pl-6 mb-6 space-y-4">
        <li>
          <p><strong>Robot Boneka</strong> - Anak &apos;memprogram&apos; gerakan robot dengan kartu perintah fisik</p>
        </li>
        <li>
          <p><strong>Game Tradisional</strong> - Permainan papan seperti &apos;Karel the Robot&apos; untuk logika dasar</p>
        </li>
        <li>
          <p><strong>Outdoor Coding</strong> - Berburu harta karun dengan petunjuk algoritmik di alam</p>
        </li>
      </ol>

      <div className="bg-green-50 p-4 rounded-lg mb-6">
        <h3 className="text-xl font-semibold mb-2">📊 Hasil yang Terlihat</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Anak usia 7 tahun mampu <b>memecahkan masalah 23% lebih cepat</b> dibanding negara lain</li>
          <li><b>Minat STEM meningkat 40%</b> pada siswa perempuan</li>
          <li>Kemampuan <b>kolaborasi tim</b> lebih baik sejak dini</li>
        </ul>
      </div>

      <h2 className="text-2xl font-bold mb-4">Pelajaran untuk Indonesia</h2>
      <p className="mb-4">
        Meski <b>infrastruktur Indonesia</b> belum setara Finlandia, prinsip dasarnya bisa diadaptasi:
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2">Prinsip</th>
              <th className="border p-2">Implementasi Finlandia</th>
              <th className="border p-2">Adaptasi Indonesia</th>
            </tr>
          </thead>
          <tbody>
            {[ 
              ["Alat Belajar", "Robot edukasi mahal", "Permainan tradisional (congklak, ular tangga)"],
              ["Guru", "Pelatihan intensif 6 bulan", "Pelatihan singkat fokus konsep dasar"],
              ["Evaluasi", "Portofolio proyek", "Observasi aktivitas bermain"],
              ["Tujuan", "Thinking skills", "Persiapan menghadapi teknologi"]
            ].map(([prinsip, finlandia, indonesia], idx) => (
              <tr key={idx}>
                <td className="border p-2">{prinsip}</td>
                <td className="border p-2">{finlandia}</td>
                <td className="border p-2">{indonesia}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold mb-4">Tantangan Implementasi</h2>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li><b>Kurikulum yang padat</b> menyisakan sedikit ruang untuk inovasi</li>
        <li><b>Keterbatasan alat</b> di daerah terpencil</li>
        <li>Persepsi <b>&quot;coding terlalu sulit untuk TK&quot;</b> di kalangan orang tua</li>
      </ul>

      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="text-xl font-semibold mb-2">💡 Inisiatif Lokal yang Menjanjikan</h3>
        <p>
          <b>TK Labschool Jakarta</b> telah memulai program unggulan:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><b>&quot;Fun Coding Day&quot;</b> bulanan dengan permainan tradisional</li>
          <li>Kolaborasi dengan <b>orang tua programmer</b> sebagai relawan</li>
          <li>Memanfaatkan <b>barang bekas</b> untuk alat peraga</li>
        </ul>
      </div>

      <p className="mt-6">
        <b>Kabar gembira:</b> Kementerian Pendidikan sedang menyusun <b>pedoman coding usia dini</b> yang akan diluncurkan 2026, terinspirasi dari Finlandia namun disesuaikan dengan <b>kearifan lokal Indonesia</b>.
      </p>
    </div>
  );
};

export default CodingDiFinlandia;
